import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  ChevronUp,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  ArrowRight,
} from "lucide-react";
import { cn } from "../../lib/utils";

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.pageYOffset > 300);
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const quickLinks = [
    { name: "Home", href: "/" },
    { name: "About Us", href: "/about-us" },
    { name: "Contact", href: "/contact" },
  ];

  const customerLinks = [
    { name: "My Account", href: "/my-account" },
    { name: "My Orders", href: "/my-orders" },
    { name: "Wishlist", href: "/product-wishlist" },
    { name: "Cart", href: "/product-cart" },
  ];

  const socialLinks = [
    { icon: Facebook, href: "https://facebook.com", label: "Facebook" },
    { icon: Instagram, href: "https://instagram.com", label: "Instagram" },
    { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
    { icon: Youtube, href: "https://youtube.com", label: "YouTube" },
  ];

  const contactInfo = [
    {
      icon: MapPin,
      text: "Shop 1,2,3, 2nd Floor, Indraprastha Complex, Varachha Main Rd, Surat",
      href: "https://maps.google.com/?q=Shop+1,2,3,+2nd+Floor,+Indraprastha+Complex,+Varachha+Main+Rd,+Surat,+Gujarat+395006",
    },
    {
      icon: Phone,
      text: "+91 97120 27909",
      href: "tel:+919712027909",
    },
    {
      icon: Mail,
      text: "cressa2024@gmail.com",
      href: "mailto:cressa2024@gmail.com",
    },
  ];

  return (
    <>
      {/* Main Footer */}
      <footer className="relative bg-gradient-to-b from-[#FDF8F0] to-[#F5EDE0] text-gray-800 overflow-hidden">
        {/* Subtle Top Accent Line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" />

        {/* Content */}
        <div className="relative z-10 pt-10 pb-6">
          <div className="container mx-auto px-6 lg:px-12">
            {/* Top Section - Logo and Newsletter */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 pb-8 border-b border-primary/20">
              <div className="flex flex-col gap-2">
                <Link to="/" className="inline-block">
                  <img
                    src="/assets/images/logo.png"
                    alt="Cressa"
                    className="h-10 w-auto object-contain"
                  />
                </Link>
                <p className="text-gray-500 text-sm max-w-xs leading-relaxed">
                  Your destination for premium beauty products.
                </p>
              </div>

              {/* App Download Badges */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://play.google.com/store/apps/details?id=com.hktech.cressa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <div className="flex items-center gap-3 bg-white hover:bg-primary/5 border border-primary/20 hover:border-primary/40 rounded-xl px-5 py-3 transition-all duration-300 shadow-sm">
                    <svg className="w-7 h-7 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
                    </svg>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider">Get it on</span>
                      <span className="text-sm font-medium text-gray-700">Google Play</span>
                    </div>
                  </div>
                </a>
                <a
                  href="https://apps.apple.com/in/app/cressa/id6743152741"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group"
                >
                  <div className="flex items-center gap-3 bg-white hover:bg-primary/5 border border-primary/20 hover:border-primary/40 rounded-xl px-5 py-3 transition-all duration-300 shadow-sm">
                    <svg className="w-7 h-7 text-gray-700" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 uppercase tracking-wider">Download on</span>
                      <span className="text-sm font-medium text-gray-700">App Store</span>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Links Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10 py-8">
              {/* Quick Links */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-dark mb-3">
                  Quick Links
                </h4>
                <ul className="space-y-2">
                  {quickLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="group flex items-center gap-2 text-gray-600 hover:text-primary-dark transition-colors duration-300"
                      >
                        <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-primary" />
                        <span className="text-sm">{link.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Customer Links */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-dark mb-3">
                  Account
                </h4>
                <ul className="space-y-2">
                  {customerLinks.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="group flex items-center gap-2 text-gray-600 hover:text-primary-dark transition-colors duration-300"
                      >
                        <ArrowRight className="w-3 h-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all duration-300 text-primary" />
                        <span className="text-sm">{link.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-dark mb-3">
                  Contact
                </h4>
                <ul className="space-y-3">
                  {contactInfo.map((item, index) => (
                    <li key={index}>
                      <a
                        href={item.href}
                        target={item.href.startsWith("http") ? "_blank" : undefined}
                        rel={item.href.startsWith("http") ? "noopener noreferrer" : undefined}
                        className="flex items-start gap-3 text-gray-600 hover:text-primary-dark transition-colors duration-300 group"
                      >
                        <div className="w-6 h-6 rounded-md bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center flex-shrink-0 transition-colors duration-300">
                          <item.icon className="w-3 h-3 text-primary-dark" />
                        </div>
                        <span className="text-xs leading-relaxed">{item.text}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Social */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-dark mb-3">
                  Follow Us
                </h4>
                <div className="flex items-center gap-2">
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="w-8 h-8 rounded-full bg-white border border-primary/20 hover:bg-primary hover:border-primary flex items-center justify-center text-gray-500 hover:text-white transition-all duration-300 shadow-sm"
                    >
                      <social.icon className="w-4 h-4" />
                    </a>
                  ))}
                </div>
                <p className="text-gray-500 text-xs mt-3 leading-relaxed">
                  Stay connected for updates.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="relative z-10 border-t border-primary/15">
          <div className="container mx-auto px-6 lg:px-12 py-4">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-gray-500">
                © {new Date().getFullYear()} Cressa. All rights reserved.
              </p>
              <div className="flex items-center gap-6">
                <Link to="/cressa_android_privacypolicy" className="text-xs text-gray-500 hover:text-primary-dark transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/account-deletion" className="text-xs text-gray-500 hover:text-primary-dark transition-colors">
                  Account Deletion
                </Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0, y: 20 }}
        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
        transition={{ duration: 0.3 }}
        onClick={scrollToTop}
        className={cn(
          "fixed right-6 bottom-6 z-50 w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center shadow-2xl shadow-primary/20 transition-transform duration-300 hover:scale-110",
          !isVisible && "pointer-events-none"
        )}
        aria-label="Scroll to top"
      >
        <ChevronUp className="w-5 h-5" />
      </motion.button>
    </>
  );
};

export default Footer;
